import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Coins, Clock, Target } from "lucide-react";
import { MARKETS, PRICE_LABEL } from "@/lib/markets";

const m = MARKETS.gold;

const BULLETS = [
  "XAU/USD levels for London & New York sessions",
  "MCX gold mapped for Indian hours",
  "Dollar index and rates correlation, explained",
];

const Room = () => {
  const navigate = useNavigate();
  return (
    <section id="room" className="relative py-28 sm:py-36" data-testid="room-section">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-16 max-w-2xl"
        >
          <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-slate-500">(03) — The Room</p>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-[#0a0a0a] sm:text-5xl">
            One room. <span className="font-accent text-grad-aurora">Full focus.</span>
          </h2>
          <p className="mt-4 text-base text-slate-600 sm:text-lg">
            No market-hopping. One membership, one room, one asset class — studied properly.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          className="glass rounded-3xl p-8 sm:p-12"
          data-testid="room-card"
        >
          <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr]">
            <div>
              <div className="flex items-center gap-4">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0a0a0a]">
                  <Coins className="h-7 w-7 text-[#F0C54A]" />
                </span>
                <div>
                  <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-[#0a0a0a]">{m.name}</h3>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">{m.arena}</p>
                </div>
              </div>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600">{m.desc}</p>
              <ul className="mt-6 space-y-3">
                {BULLETS.map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-slate-700">
                    <Target className="mt-0.5 h-4 w-4 shrink-0 text-[#0a0a0a]" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-aurora flex flex-col justify-between rounded-2xl p-6">
              <div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">Membership</p>
                <p className="mt-2 font-display text-5xl font-extrabold text-[#0a0a0a]">{PRICE_LABEL.split(" ·")[0]}</p>
                <p className="mt-1 font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">first month · cancel anytime</p>
                <p className="mt-5 flex items-center gap-2 text-sm text-slate-600">
                  <Clock className="h-4 w-4" /> London + NY sessions · daily levels
                </p>
                <p className="mt-2 font-mono2 text-[10px] uppercase tracking-widest text-slate-400">{m.stat}</p>
              </div>
              <button
                data-testid="room-join-button"
                onClick={() => navigate("/checkout")}
                className="btn-main group mt-8 flex w-full items-center justify-center gap-3 rounded-full px-6 py-4 font-sub text-sm font-bold uppercase tracking-wide"
              >
                Join the room · {PRICE_LABEL.split(" ·")[0]}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Room;
